import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { loadConfig } from '../config';
import { initSentry } from '../lib/sentry';

export function ConfigLoader({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const loadConfigWithRetry = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = await loadConfig();
      initSentry(config);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load config:', err);
      setError(err instanceof Error ? err : new Error('Unknown error loading config'));
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigWithRetry();
  }, [retryCount]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center space-y-4 py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading configuration...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <CardTitle>Configuration Error</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Failed to load application configuration. Please check your connection and try again.
            </p>
            {import.meta.env.DEV && (
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {error.message}
              </pre>
            )}
            <Button 
              onClick={() => setRetryCount(prev => prev + 1)} 
              className="w-full"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

